import React, { useState } from 'react';
import StudioBoard from '../../components/studio/StudioBoard';
import type { StoryboardShot, MusicSpan } from '../../services/studioService';

/**
 * BOARD SPIKE — the studio board on a public route, with fixture shots.
 *
 * The board itself lives behind auth inside the Studio, which makes it awkward to exercise in a real browser.
 * Unit tests prove props flow, but only a browser proves a CANVAS lays out, pans, zooms and reorders. This
 * route mounts the same component against fixtures so that check is one URL away — for us and for the
 * Playwright journey. It ships no data and reads no user state.
 */
const FIXTURE: StoryboardShot[] = [
  { line: 'She stops mid-sentence. Something in the hall is listening.', toggles: {} },
  { line: 'His hand closes on the doorframe. Too hard. The wood gives.', toggles: {}, continuesScene: true },
  // Carries take history, so the board's selects workflow (flip takes, promote one) is exercisable here.
  {
    line: 'She turns in the doorway. The light catches her eyes.', toggles: {}, continuesScene: true, transition: 'fade',
    clipPath: 'spike/shot3-v3.mp4', version: 3,
    takes: [
      { clipPath: 'spike/shot3-v2.mp4', version: 2, archived_at: '2026-08-13T10:00:00.000Z' },
      { clipPath: 'spike/shot3-v1.mp4', version: 1, archived_at: '2026-08-13T09:00:00.000Z' },
    ],
  },
  { line: 'He steps back into the rain. She does not follow.', toggles: {} },
  { line: 'Close on her hand — the mark spreading under the skin.', toggles: {}, continuesScene: true },
];

const BoardSpike: React.FC = () => {
  const [shots, setShots] = useState<StoryboardShot[]>(FIXTURE);
  const [selected, setSelected] = useState<number | null>(0);
  const [spans, setSpans] = useState<MusicSpan[]>([]);
  // The spike stands in for the two paid paths so the INTERACTIONS can be exercised without spending:
  // `assist` is the agent engine (studioAssistShot) and `remake` is the generative one (remakeShot).
  const [assisting, setAssisting] = useState<number | null>(null);
  const [remaking, setRemaking] = useState<number | null>(null);
  const stub = (i: number, set: (v: number | null) => void, text: string) => {
    set(i);
    window.setTimeout(() => {
      set(null);
      setShots((p) => p.map((s, k) => (k === i ? { ...s, line: `${text} ${s.line || ''}`.trim() } : s)));
    }, 900);
  };

  const reorder = (from: number, to: number) => setShots((prev) => {
    const next = prev.slice();
    const [s] = next.splice(from, 1);
    next.splice(to, 0, s);
    return next;
  });

  return (
    <div className="flex h-screen flex-col bg-secondary-950 text-white">
      <header className="flex items-center gap-3 border-b border-secondary-800 px-4 py-2 text-xs text-secondary-400">
        <span className="font-semibold text-white">Board spike</span>
        <span>{shots.length} shots · selected {selected === null ? 'none' : selected + 1}</span>
        <span className="ml-auto" data-testid="order">{shots.map((s) => s.line?.slice(0, 6)).join(' | ')}</span>
      </header>
      <div className="min-h-0 flex-1">
        <StudioBoard
          shots={shots}
          selectedIndex={selected}
          onSelect={setSelected}
          onReorder={reorder}
          onAddShot={shots.length < 12 ? () => setShots((p) => [...p, { toggles: {} }]) : undefined}
          atShotCap={shots.length >= 12}
          onShotChange={(i, patch) => setShots((p) => p.map((s, k) => (k === i ? { ...s, ...patch } : s)))}
          onAssistShot={(i) => stub(i, setAssisting, '[specialist]')}
          assistingIndex={assisting}
          onRemakeShot={(i) => stub(i, setRemaking, '[re-made]')}
          remakingIndex={remaking}
          onDuplicateShot={(i) => setShots((p) => [...p.slice(0, i + 1), { ...p[i] }, ...p.slice(i + 1)])}
          onQueueRender={() => { /* the spike does not spend — the panel wires the real render */ }}
          onRestoreTake={(i, clipPath) => setShots((p) => p.map((sh, k) => (k === i ? {
            ...sh,
            clipPath,
            takes: [
              ...(sh.clipPath ? [{ clipPath: sh.clipPath, version: sh.version || 0, archived_at: '' }] : []),
              ...(sh.takes || []).filter((t) => t.clipPath !== clipPath),
            ],
          } : sh)))}
          onDeleteShot={(i) => {
            setShots((p) => p.filter((_, k) => k !== i));
            setSelected((cur) => (cur == null ? null : Math.max(0, Math.min(cur, shots.length - 2))));
          }}
          musicSpans={spans}
          onMusicSpansChange={setSpans}
        />
      </div>
    </div>
  );
};

export default BoardSpike;
